import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { AlertTriangle, Repeat, Clock, Edit3, Save, X, TrendingUp, BarChart3, Sparkles, CalendarDays, LogIn, LogOut, UserCheck, UserPlus } from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyC54mPFBKqKUnuBqWYw5z4LgEje5rHiu2w",
  authDomain: "habbit-tracker-71cc3.firebaseapp.com",
  projectId: "habbit-tracker-71cc3",
  storageBucket: "habbit-tracker-71cc3.appspot.com",
  messagingSenderId: "813423900699",
  appId: "1:813423900699:web:519a8bc77cf4d56ab160be",
  measurementId: "G-KVP2ZP8N0L"
};

// --- App ID Configuration ---
const appId = (typeof window !== 'undefined' && typeof window['__app_id'] !== 'undefined')
  ? window['__app_id']
  : 'habit-tracker-app-v1';

// --- Initialize Firebase ---
let app;
let auth;
let db;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
} catch (error) {
  console.error("Error initializing Firebase:", error);
}

// --- Main Application Component ---
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [showSignInOptions, setShowSignInOptions] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentStreakStartDate, setCurrentStreakStartDate] = useState(null);
  const [longestStreak, setLongestStreak] = useState(0);
  const [relapseCount, setRelapseCount] = useState(0);
  const [relapseHistory, setRelapseHistory] = useState([]);

  const [timeElapsed, setTimeElapsed] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showConfirmRelapse, setShowConfirmRelapse] = useState(false);

  const [motivationalQuote, setMotivationalQuote] = useState("Every moment is a fresh beginning.");
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [newQuoteText, setNewQuoteText] = useState("");

  // --- Firebase Path ---
  const getTrackerDocRef = useCallback(() => {
    if (!userId) return null;
    return doc(db, `artifacts/${appId}/users/${userId}/habitTrackerData/tracker`);
  }, [userId]);

  // --- Authentication ---
  useEffect(() => {
    if (!auth) {
      setError("Firebase Auth not initialized.");
      setIsLoading(false); // Stop general loading if auth fails
      setIsAuthReady(true);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        setUserId(user.uid);
        setShowSignInOptions(false);
      } else {
        setCurrentUser(null);
        setUserId(null);
        const tokenFromWindow = (typeof window !== 'undefined' && typeof window['__initial_auth_token'] === 'string' && window['__initial_auth_token'])
          ? window['__initial_auth_token']
          : null;
        if (tokenFromWindow) {
          try {
            setAuthLoading(true);
            await signInWithCustomToken(auth, tokenFromWindow);
          } catch (authError) {
            console.error("Error signing in with custom token:", authError);
            setShowSignInOptions(true);
          } finally {
            setAuthLoading(false);
          }
        } else {
          setShowSignInOptions(true);
        }
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    if (!auth) return;
    setAuthLoading(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (authError) {
      console.error("Error signing in with Google:", authError);
      setError("Failed to sign in with Google. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAnonymousSignIn = async () => {
    if (!auth) return;
    setAuthLoading(true);
    setError(null);
    try {
      await signInAnonymously(auth);
    } catch (authError) {
      console.error("Error signing in anonymously:", authError);
      setError("Failed to sign in anonymously. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (!auth) return;
    setAuthLoading(true);
    try {
      await signOut(auth);
      setCurrentStreakStartDate(null);
      setLongestStreak(0);
      setRelapseCount(0);
      setRelapseHistory([]);
      setTimeElapsed({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setMotivationalQuote("Every moment is a fresh beginning.");
    } catch (authError) {
      console.error("Error signing out:", authError);
      setError("Failed to sign out. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  // --- Load User Data ---
  useEffect(() => {
    if (!isAuthReady) return;
    if (!userId) {
      setIsLoading(false);
      setCurrentStreakStartDate(null);
      setLongestStreak(0);
      setRelapseCount(0);
      setRelapseHistory([]);
      return;
    }
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      const docRef = getTrackerDocRef();
      if (!docRef) {
        setError("User not identified correctly. Cannot load data.");
        setIsLoading(false);
        return;
      }
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.currentStreakStartDate && data.currentStreakStartDate.toDate) {
            setCurrentStreakStartDate(data.currentStreakStartDate.toDate());
          } else if (data.currentStreakStartDate) {
            setCurrentStreakStartDate(new Date(data.currentStreakStartDate));
          } else {
            const now = new Date();
            setCurrentStreakStartDate(now);
            await setDoc(docRef, {
              currentStreakStartDate: Timestamp.fromDate(now),
              longestStreak: data.longestStreak || 0,
              relapseCount: data.relapseCount || 0,
              relapseHistory: data.relapseHistory || [],
              motivationalQuote: data.motivationalQuote || "Every moment is a fresh beginning."
            }, { merge: true });
          }
          setLongestStreak(data.longestStreak || 0);
          setRelapseCount(data.relapseCount || 0);
          setRelapseHistory((data.relapseHistory || []).map(r => ({
            ...r,
            date: r.date && r.date.toDate ? r.date.toDate() : new Date(r.date)
          })));
          setMotivationalQuote(data.motivationalQuote || "Every moment is a fresh beginning.");
          setNewQuoteText(data.motivationalQuote || "Every moment is a fresh beginning.");
        } else {
          const now = new Date();
          setCurrentStreakStartDate(now);
          await setDoc(docRef, {
            currentStreakStartDate: Timestamp.fromDate(now),
            longestStreak: 0,
            relapseCount: 0,
            relapseHistory: [],
            motivationalQuote: "Every moment is a fresh beginning."
          });
          setLongestStreak(0);
          setRelapseCount(0);
          setRelapseHistory([]);
          setMotivationalQuote("Every moment is a fresh beginning.");
          setNewQuoteText("Every moment is a fresh beginning.");
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load your progress. Please check your connection and try refreshing.");
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [isAuthReady, userId, getTrackerDocRef]);

  // --- Timer Logic ---
  useEffect(() => {
    if (!currentStreakStartDate) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = now.getTime() - currentStreakStartDate.getTime();
      if (diff < 0) {
        setTimeElapsed({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      let seconds = Math.floor(diff / 1000);
      let minutes = Math.floor(seconds / 60);
      let hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      seconds %= 60;
      minutes %= 60;
      hours %= 24;
      setTimeElapsed({ days, hours, minutes, seconds });
    }, 1000);
    return () => clearInterval(interval);
  }, [currentStreakStartDate]);

  // --- Handle Relapse (Slip-up) ---
  const handleRelapse = async () => {
    if (!currentStreakStartDate || !userId) {
      setError("Cannot record slip-up: data not fully loaded or user not identified.");
      setShowConfirmRelapse(false); return;
    }
    setIsLoading(true);
    setError(null);
    const now = new Date();
    const currentStreakDuration = Math.floor((now.getTime() - currentStreakStartDate.getTime()) / 1000);
    const newRelapseEntry = { date: Timestamp.fromDate(now), streakDuration: currentStreakDuration };
    const docRef = getTrackerDocRef();
    if (!docRef) {
      setError("User not identified. Cannot save slip-up data.");
      setIsLoading(false); setShowConfirmRelapse(false); return;
    }
    try {
      const newLongestStreak = Math.max(longestStreak, currentStreakDuration);
      await updateDoc(docRef, {
        currentStreakStartDate: Timestamp.fromDate(now),
        longestStreak: newLongestStreak,
        relapseCount: relapseCount + 1,
        relapseHistory: arrayUnion(newRelapseEntry)
      });
      setCurrentStreakStartDate(now);
      setLongestStreak(newLongestStreak);
      setRelapseCount(relapseCount + 1);
      setRelapseHistory(prev => [...prev, { ...newRelapseEntry, date: now }]);
      setTimeElapsed({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    } catch (err) {
      console.error("Error recording slip-up:", err);
      setError("Failed to record slip-up. Please try again.");
    } finally {
      setIsLoading(false); setShowConfirmRelapse(false);
    }
  };

  // --- Handle Save Quote ---
  const handleSaveQuote = async () => {
    if (!userId || !newQuoteText.trim()) {
      setIsEditingQuote(false); return;
    }
    setIsLoading(true);
    const docRef = getTrackerDocRef();
    if (!docRef) {
      setError("User not identified. Cannot save quote.");
      setIsLoading(false); return;
    }
    try {
      await updateDoc(docRef, { motivationalQuote: newQuoteText.trim() });
      setMotivationalQuote(newQuoteText.trim());
      setIsEditingQuote(false);
    } catch (err) {
      console.error("Error saving quote:", err);
      setError("Failed to save your quote. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- UI Rendering ---
  if (!isAuthReady || authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-slate-700 p-4 font-sans">
        <Clock className="w-12 h-12 sm:w-16 sm:h-16 animate-spin mb-4 text-sky-500" />
        <p className="text-lg sm:text-xl">
          {authLoading ? 'Processing authentication...' : 'Initializing connection...'}
        </p>
      </div>
    );
  }

  if (showSignInOptions && !currentUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-sky-50 via-indigo-50 to-purple-50 text-slate-800 p-4 sm:p-6 font-sans">
        <header className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-600 via-indigo-600 to-purple-600">Welcome to Habit Tracker</h1>
          <p className="text-slate-600 mt-2 sm:mt-3 text-md sm:text-lg leading-relaxed">Choose how you'd like to continue:</p>
        </header>
        {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4 sm:mb-6 text-sm w-full max-w-sm text-center">{error}</p>}
        <div className="space-y-4 w-full max-w-xs sm:max-w-sm">
          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center bg-white hover:bg-sky-50 text-sky-600 font-semibold py-3 px-4 border border-sky-300 rounded-xl shadow-md hover:shadow-lg transition-all duration-150 ease-in-out text-sm sm:text-base"
          >
            <svg className="w-5 h-5 mr-2 sm:mr-3" viewBox="0 0 24 24" fill="currentColor"><path d="M21.8 10.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.5c-.2 1.2-.9 2.2-1.9 2.9v2.4h3.1c1.8-1.7 2.9-4.1 2.9-6.9z" /><path d="M12 22c2.9 0 5.4-1 7.1-2.7l-3.1-2.4c-.9.6-2.1.9-3.5.9-2.7 0-5-1.8-5.8-4.2H3.2v2.5C4.9 19.9 8.2 22 12 22z" /><path d="M6.2 13.8c-.2-.6-.3-1.3-.3-1.9s.1-1.3.3-1.9V7.5H3.2C2.1 9.6 1.5 12 1.5 14.4c0 2.4.6 4.8 1.7 6.9l3-2.5z" /><path d="M12 6.1c1.5 0 2.8.5 3.8 1.5l2.7-2.7C16.9 3.2 14.6 2 12 2 8.2 2 4.9 4.1 3.2 6.6l3 2.5C7 6.7 9.3 4.2 12 4.2z" fillRule="evenodd" clipRule="evenodd" /></svg>
            Sign in with Google
          </button>
          <button
            onClick={handleAnonymousSignIn}
            className="w-full flex items-center justify-center bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-150 ease-in-out text-sm sm:text-base"
          >
            <UserPlus className="inline-block mr-2 sm:mr-2.5 -mt-0.5" size={18} />
            Continue Anonymously
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-6 sm:mt-8 text-center max-w-xs sm:max-w-sm leading-relaxed">
          Signing in with Google allows your data to be synced. Anonymous data is local to this browser.
        </p>
      </div>
    );
  }

  if (isLoading && userId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-slate-700 p-4 font-sans">
        <Clock className="w-12 h-12 sm:w-16 sm:h-16 animate-spin mb-4 text-sky-500" />
        <p className="text-lg sm:text-xl">Loading your progress...</p>
      </div>
    );
  }

  if (error && !showSignInOptions) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 text-red-700 p-4 sm:p-6 text-center font-sans">
        <AlertTriangle className="w-16 h-16 sm:w-20 sm:h-20 text-red-400 mb-4 sm:mb-6" />
        <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Oops! Something went wrong.</h2>
        <p className="text-md sm:text-lg mb-2">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 sm:mt-8 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 px-5 sm:py-3 sm:px-6 rounded-xl shadow-md hover:shadow-lg transition-all"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  const formatDuration = (totalSeconds) => {
    if (totalSeconds < 0) totalSeconds = 0;
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-br from-sky-50 via-indigo-50 to-purple-50 text-slate-800 p-3 sm:p-4 md:p-6 font-sans selection:bg-sky-300 selection:text-sky-900">
      <header className="w-full max-w-xl md:max-w-2xl text-center mb-6 mt-4 sm:mt-6 relative">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-600 via-indigo-600 to-purple-600">Habit Tracker</h1>
        <p className="text-slate-600 mt-1.5 sm:mt-2 text-sm sm:text-base leading-relaxed">Tracking progress, one day at a time.</p>
        {currentUser && (
          <div className="mt-2.5 text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-center sm:space-x-2">
            <span className="leading-relaxed"> {/* Added leading-relaxed here for consistency */}
              Signed in as: {currentUser.isAnonymous ? "Anonymous User" : currentUser.displayName || currentUser.email || "Google User"}
              {currentUser.isAnonymous && <span className="ml-1">(ID: {userId ? userId.substring(0, 6) : ''}...)</span>}
            </span>
            <button
              onClick={handleSignOut}
              disabled={authLoading}
              className="mt-1 sm:mt-0 text-rose-500 hover:text-rose-700 font-medium text-xs py-1 px-2 rounded-md hover:bg-rose-100/70 transition-colors"
              title="Sign Out"
            >
              <LogOut size={14} className="inline -mt-0.5 mr-0.5" /> Sign Out
            </button>
          </div>
        )}
      </header>

      <main className="w-full max-w-xl md:max-w-2xl bg-white/90 backdrop-blur-lg shadow-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        {/* Current Streak Timer */}
        <section className="text-center p-4 sm:p-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl shadow-lg text-white">
          <h2 className="text-lg sm:text-xl font-semibold opacity-90 mb-1.5 sm:mb-2">Current Streak</h2>
          <div className="tracking-normal">
            <span className="text-4xl sm:text-5xl md:text-6xl font-bold">{String(timeElapsed.days).padStart(2, '0')}</span><span className="text-xl sm:text-2xl opacity-80 ml-1">d</span>
            <span className="text-4xl sm:text-5xl md:text-6xl font-bold ml-1.5 sm:ml-2 md:ml-3">{String(timeElapsed.hours).padStart(2, '0')}</span><span className="text-xl sm:text-2xl opacity-80 ml-1">h</span>
            <span className="text-4xl sm:text-5xl md:text-6xl font-bold ml-1.5 sm:ml-2 md:ml-3">{String(timeElapsed.minutes).padStart(2, '0')}</span><span className="text-xl sm:text-2xl opacity-80 ml-1">m</span>
            <span className="text-2xl sm:text-3xl md:text-4xl font-bold ml-1.5 sm:ml-2 md:ml-3">{String(timeElapsed.seconds).padStart(2, '0')}</span><span className="text-lg sm:text-xl opacity-80 ml-1">s</span>
          </div>
          {currentStreakStartDate && (
            <p className="text-xs opacity-80 mt-2 sm:mt-3 leading-relaxed"> {/* Added leading-relaxed */}
              Started: {currentStreakStartDate.toLocaleDateString()} {currentStreakStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </section>

        {/* Slip-up Button */}
        <section className="text-center">
          <button
            onClick={() => setShowConfirmRelapse(true)}
            disabled={isLoading || authLoading}
            className="w-full sm:w-auto bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold py-3 sm:py-3.5 px-8 sm:px-10 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-pink-300 focus:ring-opacity-75 disabled:opacity-60 disabled:transform-none disabled:hover:shadow-lg text-sm sm:text-base"
          >
            <Repeat className="inline-block mr-2 sm:mr-2.5 -mt-0.5" size={18} />
            Log a Slip-up
          </button>
        </section>

        {/* Statistics */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-lg text-white hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center mb-1 sm:mb-1.5">
              <TrendingUp size={20} className="mr-1.5 sm:mr-2 opacity-90" />
              <h3 className="text-md sm:text-lg font-semibold opacity-95">Longest Streak</h3>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-right tracking-tight">{formatDuration(longestStreak)}</p>
          </div>
          <div className="bg-gradient-to-br from-sky-500 to-cyan-600 p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-lg text-white hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center mb-1 sm:mb-1.5">
              <BarChart3 size={20} className="mr-1.5 sm:mr-2 opacity-90" />
              <h3 className="text-md sm:text-lg font-semibold opacity-95">Total Slip-ups</h3>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-right tracking-tight">{relapseCount}</p>
          </div>
        </section>

        {/* Motivational Quote */}
        <section className="bg-white/80 p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-lg border border-slate-200/70">
          <div className="flex justify-between items-center mb-2 sm:mb-2.5">
            <div className="flex items-center">
              <Sparkles size={20} className="mr-2 sm:mr-2.5 text-indigo-500" />
              <h3 className="text-md sm:text-lg font-semibold text-indigo-700">Daily Focus</h3>
            </div>
            {!isEditingQuote ? (
              <button onClick={() => { setIsEditingQuote(true); setNewQuoteText(motivationalQuote); }} className="text-indigo-500 hover:text-indigo-700 p-1 sm:p-1.5 rounded-md sm:rounded-lg hover:bg-indigo-100/70 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400">
                <Edit3 size={16} />
              </button>
            ) : (
              <div className="flex space-x-1.5 sm:space-x-2">
                <button onClick={handleSaveQuote} disabled={isLoading || authLoading} className="text-green-500 hover:text-green-600 p-1 sm:p-1.5 rounded-md sm:rounded-lg hover:bg-green-100/70 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50">
                  <Save size={16} />
                </button>
                <button onClick={() => setIsEditingQuote(false)} className="text-red-500 hover:text-red-600 p-1 sm:p-1.5 rounded-md sm:rounded-lg hover:bg-red-100/70 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400">
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
          {isEditingQuote ? (
            <textarea
              value={newQuoteText}
              onChange={(e) => setNewQuoteText(e.target.value)}
              className="w-full p-2.5 sm:p-3 rounded-lg bg-slate-50/80 text-slate-700 border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-150 shadow-sm text-sm sm:text-base leading-relaxed" /* Added leading-relaxed */
              rows="3"
              placeholder="Enter your motivational quote or focus..."
            />
          ) : (
            <p className="text-slate-600 italic text-sm leading-relaxed pl-0.5 sm:pl-1">"{motivationalQuote}"</p>
          )}
        </section>

        {/* Progress Journal */}
        {relapseHistory.length > 0 && (
          <section>
            <div className="flex items-center justify-center mb-2.5 sm:mb-3">
              <CalendarDays size={20} className="mr-2 sm:mr-2.5 text-slate-600" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-700">Progress Journal</h3>
            </div>
            <div className="max-h-60 sm:max-h-72 overflow-y-auto bg-white/80 border border-slate-200/70 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-inner space-y-2.5 sm:space-y-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
              {relapseHistory.slice().sort((a, b) => b.date.getTime() - a.date.getTime()).map((entry, index) => (
                <div key={index} className="p-3 bg-slate-100/90 rounded-lg sm:rounded-xl border border-slate-200/80 text-xs sm:text-sm shadow-sm">
                  <p className="font-semibold text-rose-600 leading-relaxed">Slip-up on: {entry.date.toLocaleDateString()} at {entry.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p> {/* Added leading-relaxed */}
                  <p className="text-slate-500 mt-0.5 leading-relaxed">Previous Streak: {formatDuration(entry.streakDuration)}</p> {/* Added leading-relaxed */}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="w-full max-w-xl md:max-w-2xl text-center mt-8 sm:mt-10 mb-4 sm:mb-6">
        <p className="text-xs text-slate-500 leading-relaxed"> {/* Added leading-relaxed */}
          This is a journey of strength. Each step forward counts.
        </p>
      </footer>

      {/* Slip-up Confirmation Modal */}
      {showConfirmRelapse && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out animate-fadeIn">
          <div className="bg-white p-5 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl max-w-sm sm:max-w-md w-full text-center transform transition-all duration-300 ease-out animate-scaleUp">
            <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 text-rose-500 mx-auto mb-3 sm:mb-5" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-slate-800">Confirm Slip-up</h2>
            <p className="text-slate-600 mb-5 sm:mb-7 text-sm sm:text-base leading-relaxed"> {/* Added leading-relaxed */}
              Recording a slip-up helps maintain honesty on your path. Are you sure? This will reset your current streak.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-2.5 sm:space-y-0 sm:space-x-3 md:space-x-4">
              <button
                onClick={() => setShowConfirmRelapse(false)}
                className="bg-slate-200 hover:bg-slate-300/80 text-slate-700 font-medium py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-slate-400 w-full sm:w-auto text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleRelapse}
                disabled={isLoading || authLoading}
                className="bg-rose-500 hover:bg-rose-600 text-white font-semibold py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-150 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-rose-400 w-full sm:w-auto text-sm sm:text-base"
              >
                {(isLoading || authLoading) ? 'Processing...' : 'Yes, I Slipped Up'}
              </button>
            </div>
          </div>
        </div>
      )}
      <style jsx global>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleUp {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
                .animate-scaleUp { animation: scaleUp 0.3s ease-out forwards; }
                
                .scrollbar-thin { scrollbar-width: thin; } 
                .scrollbar-thumb-slate-300::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 6px;}
                .scrollbar-track-slate-100::-webkit-scrollbar-track { background-color: #f1f5f9; border-radius: 6px;}
                ::-webkit-scrollbar { width: 8px; height: 8px; } 
            `}</style>
    </div>
  );
}

export default App;
